/**
 * Palette showcase — mirrors `src/design-system/foundations/_palette-maps.scss` only
 * (`$ds-palette-*`, `$ds-palettes-gradient`). No tokens outside those maps.
 * CSS variables: `--pal-{family}-{step}` from `colors.scss`.
 */

const PURPLE = {
  50: '#F2C2FF',
  100: '#EEA2FF',
  200: '#EA8DFF',
  300: '#E679FF',
  400: '#D84DFF',
  500: '#DA35FF',
  600: '#D929F8',
  700: '#C224E5',
  800: '#BD18DA',
  900: '#A514BE',
  1000: '#9110A8',
  a50: 'rgba(216, 77, 255, 0.1)',
  a100: 'rgba(216, 77, 255, 0.17)',
  a150: 'rgba(216, 77, 255, 0.26)',
  a200: 'rgba(216, 77, 255, 0.32)',
  a300: 'rgba(243, 221, 255, 0.17)',
};

const BLUE = {
  50: '#C2CCFF',
  100: '#A2B2FF',
  200: '#8C9FFF',
  300: '#7994FF',
  400: '#4D7FFF',
  500: '#3557FF',
  600: '#293EF8',
  700: '#2437E5',
  800: '#2818DA',
  900: '#142BBE',
  1000: '#101AA8',
  a50: 'rgba(77, 127, 255, 0.1)',
  a100: 'rgba(77, 127, 255, 0.17)',
  a150: 'rgba(77, 127, 255, 0.26)',
  a200: 'rgba(77, 127, 255, 0.32)',
  a300: 'rgba(221, 229, 255, 0.17)',
};

const GREEN = {
  50: '#ABFFAF',
  100: '#92FF9B',
  200: '#7CFF87',
  300: '#64FF69',
  400: '#1BEA60',
  500: '#12D854',
  600: '#0AD04C',
  700: '#0FC64C',
  800: '#0FBB48',
  900: '#0EAD43',
  1000: '#0E9B3D',
  a50: 'rgba(15, 198, 76, 0.1)',
  a100: 'rgba(15, 198, 76, 0.17)',
  a150: 'rgba(15, 198, 76, 0.26)',
  a200: 'rgba(15, 198, 76, 0.32)',
  a300: 'rgba(221, 255, 240, 0.17)',
};

const ORANGE = {
  50: '#FFE5AB',
  100: '#FFDE92',
  200: '#FFD77C',
  300: '#FFD165',
  400: '#FFC94B',
  500: '#FFC234',
  600: '#FBBA23',
  700: '#ECAB14',
  800: '#DBA118',
  900: '#CB9619',
  1000: '#B88817',
  a50: 'rgba(236, 171, 20, 0.1)',
  a100: 'rgba(236, 171, 20, 0.17)',
  a150: 'rgba(236, 171, 20, 0.26)',
  a200: 'rgba(236, 171, 20, 0.32)',
  a300: 'rgba(255, 247, 221, 0.17)',
};

const RED = {
  50: '#FFC2C2',
  100: '#FFA2A2',
  200: '#FF8C8C',
  300: '#FF7979',
  400: '#FF4B4B',
  500: '#FF3535',
  600: '#F82929',
  700: '#E52424',
  800: '#DA1818',
  900: '#BE1414',
  1000: '#A81010',
  a50: 'rgba(255, 75, 75, 0.1)',
  a100: 'rgba(255, 75, 75, 0.17)',
  a150: 'rgba(255, 75, 75, 0.26)',
  a200: 'rgba(255, 75, 75, 0.32)',
  a300: 'rgba(255, 221, 221, 0.17)',
};

const VIOLET = {
  50: '#E3C4FF',
  100: '#DAB1FF',
  200: '#D09CFF',
  300: '#C381FF',
  400: '#AB4DFF',
  500: '#931CFF',
  600: '#870BFB',
  700: '#8008EF',
  800: '#7500E3',
  900: '#6C00D0',
  1000: '#6200BD',
  a50: 'rgba(128, 8, 239, 0.1)',
  a100: 'rgba(128, 8, 239, 0.17)',
  a150: 'rgba(128, 8, 239, 0.26)',
  a200: 'rgba(128, 8, 239, 0.32)',
  a300: 'rgba(230, 204, 255, 0.17)',
};

const GREY_SLATE = {
  50: '#D7DCF3',
  100: '#C9CFE9',
  200: '#B5BCDB',
  300: '#A2A9C8',
  400: '#9AA1C0',
  500: '#868DAB',
  600: '#6E7591',
  700: '#61677F',
  800: '#565A6E',
  900: '#45495A',
  1000: '#32343F',
  a50: 'rgba(97, 103, 127, 0.1)',
  a100: 'rgba(97, 103, 127, 0.17)',
  a150: 'rgba(97, 103, 127, 0.26)',
  a200: 'rgba(97, 103, 127, 0.32)',
};

const GREY_BLACK = {
  50: '#F0F0F0',
  100: '#D1D1D1',
  200: '#BCBCBC',
  300: '#ACACAC',
  400: '#8E8E8E',
  500: '#7A7A7A',
  600: '#646464',
  700: '#525252',
  800: '#3E3E3E',
  900: '#252525',
  1000: '#0D101C',
  a50: 'rgba(82, 82, 82, 0.1)',
  a100: 'rgba(82, 82, 82, 0.17)',
  a150: 'rgba(82, 82, 82, 0.26)',
  a200: 'rgba(82, 82, 82, 0.32)',
};

const GREY_PURPLE = {
  50: '#E8C3FF',
  100: '#E1B1FF',
  200: '#D298F5',
  300: '#C991EB',
  400: '#BC85DE',
  500: '#B37ED4',
  600: '#A976C8',
  700: '#8F63AA',
  800: '#81599A',
  900: '#6E4E82',
  1000: '#654877',
  a50: 'rgba(143, 99, 170, 0.1)',
  a100: 'rgba(143, 99, 170, 0.17)',
  a150: 'rgba(143, 99, 170, 0.26)',
  a200: 'rgba(143, 99, 170, 0.32)',
};

const GREY_BLUE = {
  50: '#F4F7FE',
  100: '#EBF0FA',
  200: '#E6EBF8',
  300: '#DCE1EF',
  400: '#CDD3E4',
  500: '#C0C8DC',
  600: '#B9C1D5',
  700: '#ACB5CD',
  800: '#97A3C2',
  900: '#8F9BBA',
  1000: '#8C96B0',
  a50: 'rgba(172, 181, 205, 0.1)',
  a100: 'rgba(172, 181, 205, 0.17)',
  a150: 'rgba(172, 181, 205, 0.26)',
  a200: 'rgba(172, 181, 205, 0.32)',
};

const GREY_WHITE = {
  50: '#FFFFFF',
  100: '#FDFDFD',
  200: '#F9F9FB',
  300: '#F5F5F5',
  400: '#EFEFEF',
  500: '#E5E5E5',
  600: '#DFDFDF',
  700: '#D6D6D6',
  800: '#CDCDCD',
  900: '#C8C8C8',
  1000: '#BFBFBF',
  a50: 'rgba(214, 214, 214, 0.1)',
  a100: 'rgba(214, 214, 214, 0.17)',
  a150: 'rgba(214, 214, 214, 0.26)',
  a200: 'rgba(214, 214, 214, 0.32)',
};

const TYPO_BLACK = {
  50: '#8A8A8A',
  100: '#717171',
  200: '#656565',
  300: '#545454',
  400: '#464646',
  500: '#3C3C3C',
  600: '#333333',
  700: '#2E2E2E',
  800: '#292A2D',
  900: '#202125',
  1000: '#0D101C',
  a50: 'rgba(36, 36, 36, 0.1)',
  a100: 'rgba(36, 36, 36, 0.17)',
  a150: 'rgba(36, 36, 36, 0.26)',
  a200: 'rgba(36, 36, 36, 0.32)',
};

const NEUTRAL_LIGHT = {
  '000': '#F2DDFF',
  '050': '#EAC8FF',
  100: '#E3B4FF',
  200: '#DCA9FC',
  300: '#CF95F2',
  400: '#C68CE8',
  500: '#B780D8',
  600: '#A976C8',
  700: '#A06EBE',
  800: '#8F63AA',
  900: '#805A98',
  1000: '#704F84',
  1100: '#634774',
  1200: '#503C5D',
  1300: '#3D2E47',
  a50: 'rgba(242, 221, 255, 0.1)',
  a100: 'rgba(242, 221, 255, 0.17)',
  a150: 'rgba(242, 221, 255, 0.26)',
  a200: 'rgba(242, 221, 255, 0.32)',
};

const NEUTRAL_DARK = {
  1300: '#000000',
  1200: '#0F0D11',
  1100: '#1F1A23',
  1000: '#251E2B',
  900: '#32263B',
  800: '#392C41',
  700: '#41334A',
  600: '#473751',
  500: '#51405C',
  400: '#5D496A',
  300: '#685276',
  200: '#7A6289',
  100: '#836A93',
  50: '#9479A6',
  accent: '#A487B7',
  a50: 'rgba(164, 135, 183, 0.1)',
  a100: 'rgba(164, 135, 183, 0.17)',
  a150: 'rgba(164, 135, 183, 0.26)',
  a200: 'rgba(164, 135, 183, 0.32)',
};

const MONO_ORDER = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 'a50', 'a100', 'a150', 'a200', 'a300'];

const GREY_ORDER = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 'a50', 'a100', 'a150', 'a200'];

const NEUTRAL_LIGHT_ORDER = [
  '000',
  '050',
  100,
  200,
  300,
  400,
  500,
  600,
  700,
  800,
  900,
  1000,
  1100,
  1200,
  1300,
  'a50',
  'a100',
  'a150',
  'a200',
];

const NEUTRAL_DARK_ORDER = [
  1300,
  1200,
  1100,
  1000,
  900,
  800,
  700,
  600,
  500,
  400,
  300,
  200,
  100,
  50,
  'accent',
  'a50',
  'a100',
  'a150',
  'a200',
];

/**
 * @param {string} family - `$ds-palettes` key
 * @param {string} columnTitle - column header (e.g. "Blue")
 * @param {Record<string, string>} map
 * @param {(string|number)[]} order
 */
function buildPaletteColumn(family, columnTitle, map, order) {
  const solid = [];
  const alpha = [];
  order.forEach((step) => {
    if (map[step] == null) return;
    const stepStr = String(step);
    const value = map[step];
    const cssVar = `${family}-${stepStr}`;
    const rowLabel = `${columnTitle} - ${stepStr}`;
    const row = { step: stepStr, rowLabel, value, cssVar };
    if (typeof step === 'string' && step.startsWith('a')) {
      alpha.push(row);
    } else {
      solid.push(row);
    }
  });
  return { kind: 'palette', family, label: columnTitle, solid, alpha };
}

export const dsGradientShowcase = [
  {
    family: 'linear-purple',
    label: 'Linear purple',
    steps: {
      100: 'linear-gradient(180deg, #8f63aa 0%, #6e247f 102.5%)',
      90: 'linear-gradient(180deg, rgba(143, 99, 170, 0.9) 0%, rgba(110, 36, 127, 0.9) 102.5%)',
      80: 'linear-gradient(180deg, rgba(143, 99, 170, 0.8) 0%, rgba(110, 36, 127, 0.8) 102.5%)',
      70: 'linear-gradient(180deg, rgba(143, 99, 170, 0.7) 0%, rgba(110, 36, 127, 0.7) 102.5%)',
    },
  },
  {
    family: 'linear-green',
    label: 'Linear green',
    steps: {
      100: 'linear-gradient(180deg, #10b981 0%, #059669 102.5%)',
      90: 'linear-gradient(180deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 102.5%)',
      80: 'linear-gradient(180deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 102.5%)',
      70: 'linear-gradient(180deg, rgba(16, 185, 129, 0.7) 0%, rgba(5, 150, 105, 0.7) 102.5%)',
    },
  },
  {
    family: 'linear-orange',
    label: 'Linear orange',
    steps: {
      100: 'linear-gradient(180deg, #f59e0b 0%, #d97706 102.5%)',
      90: 'linear-gradient(180deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 102.5%)',
      80: 'linear-gradient(180deg, rgba(245, 158, 11, 0.8) 0%, rgba(217, 119, 6, 0.8) 102.5%)',
      70: 'linear-gradient(180deg, rgba(245, 158, 11, 0.7) 0%, rgba(217, 119, 6, 0.7) 102.5%)',
    },
  },
  {
    family: 'linear-light',
    label: 'Linear light',
    steps: {
      100: 'linear-gradient(180deg, #ffffff 0%, #faf1ff 102.5%)',
    },
  },
  {
    family: 'linear-white',
    label: 'Linear white',
    steps: {
      100: 'linear-gradient(180deg, #ffffff 0%, #faf1ff 102.5%)',
    },
  },
];

function buildLinearStackColumn() {
  const rows = [];
  dsGradientShowcase.forEach((g) => {
    Object.entries(g.steps)
      .sort(([a], [b]) => Number(b) - Number(a))
      .forEach(([step, gradient]) => {
        const stepStr = String(step);
        rows.push({
          rowLabel: 'Linear',
          subLabel: g.label,
          value: gradient,
          cssVar: `${g.family}-${stepStr}`,
        });
      });
  });
  return { kind: 'linear-stack', family: '__linear__', label: 'Linear', rows };
}

export const dsPaletteShowcaseGroups = [
  {
    id: 'monochromatic',
    title: 'Monochromatic palette',
    description:
      'Solid ramps 50–1000, then alpha a50–a300. Variables `--pal-{family}-{step}` from `_palette-maps.scss`.',
    columns: [
      buildPaletteColumn('purple', 'Purple', PURPLE, MONO_ORDER),
      buildPaletteColumn('blue', 'Blue', BLUE, MONO_ORDER),
      buildPaletteColumn('green', 'Green', GREEN, MONO_ORDER),
      buildPaletteColumn('orange', 'Orange', ORANGE, MONO_ORDER),
      buildPaletteColumn('red', 'Red', RED, MONO_ORDER),
      buildPaletteColumn('violet', 'Violet', VIOLET, MONO_ORDER),
    ],
  },
  {
    id: 'greyscale',
    title: 'Greyscale palette',
    description: 'Cool slate, warm grey-black, tinted ramps, white neutrals, and linear gradients.',
    columns: [
      buildPaletteColumn('grey-slate', 'Grey slate', GREY_SLATE, GREY_ORDER),
      buildPaletteColumn('grey-black', 'Grey black', GREY_BLACK, GREY_ORDER),
      buildPaletteColumn('grey-purple', 'Grey purple', GREY_PURPLE, GREY_ORDER),
      buildPaletteColumn('grey-blue', 'Grey blue', GREY_BLUE, GREY_ORDER),
      buildPaletteColumn('grey-white', 'Grey white', GREY_WHITE, GREY_ORDER),
      buildLinearStackColumn(),
    ],
  },
  {
    id: 'type',
    title: 'Typo color palette',
    description: 'Typography ramps from `$ds-palette-typo-black` and `$ds-palette-typo-grey-black`.',
    columns: [
      buildPaletteColumn('black', 'Black', TYPO_BLACK, GREY_ORDER),
      buildPaletteColumn('grey-black', 'Grey black', GREY_BLACK, GREY_ORDER),
    ],
  },
  {
    id: 'neutral',
    title: 'Neutral palette',
    description: 'Brand-tinted neutrals from `$ds-palette-neutral-light` and `$ds-palette-neutral-dark`.',
    columns: [
      buildPaletteColumn('neutral-light', 'Neutral light', NEUTRAL_LIGHT, NEUTRAL_LIGHT_ORDER),
      buildPaletteColumn('neutral-dark', 'Neutral dark', NEUTRAL_DARK, NEUTRAL_DARK_ORDER),
    ],
  },
];
